import * as React from 'react'

import { Features } from 'generated/featureDefinitions'
import { WorkTimeCategoriesPageDocument } from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { CustomTimeRangeProvider } from 'modules/attendance/hooks/useCustomTimeRange'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { AttendanceSettingWorkCategoriesProvider } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { EMPLOYEE_TYPE } from 'modules/attendance/lib/types'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import BankOfHoursCounter from './Content/BankOfHoursCounter'
import ComplementaryHoursRules from './Content/ComplementaryHoursRules'
import ExtraAndComplementaryHoursCalculation from './Content/ExtraAndComplementaryHoursCalculation'
import ExtraHoursBaseline from './Content/ExtraHoursBaseline'
import ExtraHoursCalculation from './Content/ExtraHoursCalculation'
import ExtraHoursRules from './Content/ExtraHoursRules'
import SpecialWorkHours from './Content/SpecialWorkHours'

interface Props {
  attendanceSetting: AttendanceSetting
}

const WorkTimeCategories = ({ attendanceSetting }: Props) => {
  const { company } = useCurrentSession()
  const companyLegalEntityIds = company.legalEntities.map((entity) => entity.id)
  const payrollFeature = 'payroll_bundle'
  const { data, loading, refetch } = useQuery(WorkTimeCategoriesPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: [
        payrollFeature,
        TimesheetFeatures.BANK_OF_HOURS,
        TimesheetFeatures.BANK_OF_HOURS_TRANSFER_FREQUENCY,
        TimesheetFeatures.EXTRA_HOUR_RULES,
        TimesheetFeatures.SPECIAL_HOURS,
        TimesheetFeatures.CONTRACT_HOURS_BASELINE,
        TimesheetFeatures.DEV_CONTRACT_HOURS_BASELINE,
        TimesheetFeatures.COMPLEMENTARY_HOURS,
      ],
      legalEntityIds: companyLegalEntityIds,
      reimbursableTypes: ['TimeSettings::CustomTimeRangeCategory'],
      settingId: attendanceSetting.id,
    },
  })

  const { featureAvailability: hasSpecialHoursBrazil } = useAttendanceFeatures(
    Features.time_tracking_special_hours_brazil
  )
  const { featureAvailability: hasCompensations } = useAttendanceFeatures(
    Features.compensations_management
  )

  const timeSettingsCategories = data?.apiCore.company?.timeSettingsCustomTimeRangeCategories ?? []
  const allExtraHoursRanges =
    data?.attendance?.employeesSetting?.extrahoursrangeConnection?.nodes ?? []
  const extraHoursRanges = allExtraHoursRanges.filter(
    (range) => range.employeeType === EMPLOYEE_TYPE.FULL_TIME
  )
  const complementaryHoursRanges = allExtraHoursRanges.filter(
    (range) => range.employeeType === EMPLOYEE_TYPE.PART_TIME
  )
  const features = data?.features.featuresConnection.nodes ?? []
  const featureByName = groupBy(features, (feature) => feature.name)
  const payrollMappingsTaxonomies = data?.payroll.reimbursableSupplementTaxonomies ?? []
  const payrollMappingsCompensations =
    data?.compensations.reimbursablePayrollConceptsConnection.nodes ?? []
  const contractTaxonomies = data?.contracts.taxonomies ?? []
  const payrollConcepts = data?.payroll.conceptsConnection.nodes ?? []
  const canCreateSettingCategory = !!data?.timeSettings._policies.manageTimeRangeCategories.can
  const payrollEnabled = payrollFeature in featureByName
  const customTimeRanges = data?.attendance.employeesSetting?.customTimeRanges ?? []
  const specialHoursRegularHoursCompensation =
    data?.attendance.employeesSetting?.specialHoursRegularHoursCompensation ?? false
  const balanceCarryover = data?.attendance.employeesSetting?.balanceCarryover ?? undefined
  const overtimePools = data?.attendance?.overtimePoolsConnection.nodes ?? []

  const hasBankOfHoursTransferFrequency =
    TimesheetFeatures.BANK_OF_HOURS_TRANSFER_FREQUENCY in featureByName
  const hasBankOfHours =
    TimesheetFeatures.BANK_OF_HOURS in featureByName && !hasBankOfHoursTransferFrequency
  const hasExtraHourRules = TimesheetFeatures.EXTRA_HOUR_RULES in featureByName
  const hasSpecialHours = TimesheetFeatures.SPECIAL_HOURS in featureByName
  const hasContractHoursBaseline =
    TimesheetFeatures.CONTRACT_HOURS_BASELINE in featureByName ||
    TimesheetFeatures.DEV_CONTRACT_HOURS_BASELINE in featureByName
  const hasComplementaryHours = TimesheetFeatures.COMPLEMENTARY_HOURS in featureByName

  return (
    <CustomTimeRangeProvider>
      <AttendanceSettingWorkCategoriesProvider
        value={{
          attendanceSetting,
          canCreateSettingCategory,
          companyId: company.id,
          contractTaxonomies,
          payrollConcepts,
          customTimeRanges,
          extraHoursRanges,
          complementaryHoursRanges,
          loading,
          payrollEnabled,
          payrollMappingsTaxonomies,
          payrollMappingsCompensations,
          timeSettingsCategories,
          specialHoursRegularHoursCompensation,
          hasBankOfHours,
          hasCompensations,
          hasContractHoursBaseline,
          refetch,
        }}
      >
        {hasBankOfHours && <ExtraHoursBaseline attendanceSetting={attendanceSetting} />}
        {hasBankOfHoursTransferFrequency && !hasComplementaryHours && (
          <ExtraHoursCalculation
            attendanceSetting={attendanceSetting}
            balanceCarryover={balanceCarryover}
            overtimePools={overtimePools}
          />
        )}
        {hasComplementaryHours && (
          <>
            <ExtraAndComplementaryHoursCalculation attendanceSetting={attendanceSetting} />
            {hasBankOfHoursTransferFrequency && (
              <BankOfHoursCounter
                attendanceSetting={attendanceSetting}
                balanceCarryover={balanceCarryover}
                overtimePools={overtimePools}
              />
            )}
          </>
        )}
        {hasComplementaryHours &&
          hasExtraHourRules &&
          attendanceSetting.enableComplementaryHours && <ComplementaryHoursRules />}
        {hasExtraHourRules && <ExtraHoursRules />}
        {hasSpecialHours && <SpecialWorkHours hasSpecialHoursBrazil={hasSpecialHoursBrazil} />}
      </AttendanceSettingWorkCategoriesProvider>
    </CustomTimeRangeProvider>
  )
}

export default observer(WorkTimeCategories)
